import React, { useState, useEffect } from "react";
import { T } from "../lib/theme";

const MIN_PASSWORD_LENGTH = 8;

/* ─── Reset Password ──────────────────────────────────────
   Reached only via a ?reset=<token> link an admin generated and sent
   manually (see functions/api/admin/reset-token.js — this project has
   no email-sending capability). Validates the token on mount via
   GET /api/reset/validate, then lets the user set a new password via
   POST /api/reset/complete. Doesn't touch sessionStorage/hf_auth at
   all — completing a reset doesn't log anyone in, it just clears the
   way to sign in normally afterward, same as a fresh signup does. */
export function ResetPasswordScreen({token}){
  const[checking,setChecking]=useState(true);
  const[tokenValid,setTokenValid]=useState(false);
  const[password,setPassword]=useState("");
  const[confirmPassword,setConfirmPassword]=useState("");
  const[err,setErr]=useState("");
  const[info,setInfo]=useState("");
  const[busy,setBusy]=useState(false);
  const[done,setDone]=useState(false);

  useEffect(()=>{
    let cancelled=false;
    fetch(`/api/reset/validate?token=${encodeURIComponent(token||"")}`)
      .then(res=>res.json())
      .then(d=>{if(!cancelled){setTokenValid(!!d.valid);setChecking(false);}})
      .catch(()=>{if(!cancelled){setTokenValid(false);setChecking(false);}});
    return()=>{cancelled=true;};
  },[token]);

  const submit=async(e)=>{
    e.preventDefault();
    setErr("");
    if(password.length<MIN_PASSWORD_LENGTH){setErr(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);return;}
    if(password!==confirmPassword){setErr("Passwords don't match.");return;}

    setBusy(true);
    try{
      const res=await fetch("/api/reset/complete",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({token,password})});
      const d=await res.json().catch(()=>({}));
      if(res.ok&&d.ok){
        setDone(true);
        setInfo("Your password has been updated. Redirecting to sign in...");
        setTimeout(()=>{window.location.href="/";},1800);
      }else{
        setErr(d.error||"Could not reset your password — the link may have expired.");
      }
    }catch{
      setErr("Connection error — try again.");
    }
    setBusy(false);
  };

  const fieldStyle={width:"100%",boxSizing:"border-box",padding:"11px 12px",border:`1.5px solid ${T.border}`,borderRadius:8,fontSize:14,marginBottom:10,color:T.text,background:T.card};

  return(
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:T.bg,fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
      <div style={{background:T.card,borderRadius:16,padding:32,maxWidth:340,width:"100%",boxShadow:T.shadow}}>
        <div style={{fontSize:20,fontWeight:800,marginBottom:4,color:T.text}}>HomeFleet</div>
        <div style={{fontSize:13,color:T.textSm,marginBottom:20}}>Reset your password.</div>

        {checking&&<div style={{fontSize:13,color:T.textMuted}}>Checking your link...</div>}

        {!checking&&!tokenValid&&(
          <>
            <div style={{fontSize:13,color:T.red,marginBottom:14}}>This reset link is invalid or has expired. Ask the owner to send a new one.</div>
            <a href="/" style={{fontSize:13,color:T.teal,fontWeight:700,textDecoration:"none"}}>Return to sign in</a>
          </>
        )}

        {!checking&&tokenValid&&!done&&(
          <form onSubmit={submit} noValidate>
            <input type="password" value={password} onChange={e=>setPassword(e.target.value)} autoFocus placeholder="New password" autoComplete="new-password" style={fieldStyle}/>
            <input type="password" value={confirmPassword} onChange={e=>setConfirmPassword(e.target.value)} placeholder="Confirm new password" autoComplete="new-password" style={fieldStyle}/>
            {err&&<div style={{fontSize:13,color:T.red,marginBottom:10}}>{err}</div>}
            <button type="submit" disabled={busy} style={{width:"100%",padding:"11px 0",background:`linear-gradient(135deg,${T.teal},#0f766e)`,color:"#fff",border:"none",borderRadius:8,fontWeight:700,fontSize:14,opacity:busy?.6:1}}>
              {busy?"Saving...":"Set New Password"}
            </button>
          </form>
        )}

        {done&&<div style={{fontSize:13,color:T.amber,background:T.amberL,border:`1px solid ${T.amberBorderStrong}`,borderRadius:8,padding:"9px 12px"}}>{info}</div>}
      </div>
    </div>
  );
}
